import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Database, AlertTriangle, Clock } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { marginCorrectionsApi } from '../services/marginCorrectionsApi';
import { useToast } from '../components/ui/Toast';
import { InvoiceLinesTab } from '../components/margin-corrections/InvoiceLinesTab';
import { CorrectionsTab } from '../components/margin-corrections/CorrectionsTab';
import { SpecialLotsTab } from '../components/margin-corrections/SpecialLotsTab';
import type { MCTab, MCFilterOptions, LotOriginStatus } from '../types/marginCorrections';

const TABS: { key: MCTab; label: string }[] = [
    { key: 'invoice-lines', label: 'Invoice Lines' },
    { key: 'corrections', label: 'Corrections' },
    { key: 'special-lots', label: 'Special-Price Lots' },
];

const COMPANY = 'dit'; // Phase 1 is dit only; the design generalises.

export function MarginCorrectionsPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/margin-corrections');
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState<MCTab>('invoice-lines');
    const [refreshKey, setRefreshKey] = useState(0);
    const [filterOptions, setFilterOptions] = useState<MCFilterOptions | null>(null);
    const [lotStatus, setLotStatus] = useState<LotOriginStatus | null>(null);
    const [rebuilding, setRebuilding] = useState(false);
    // Auto-refresh-if-stale must fire at most once per mount, or a persistently failing rebuild
    // would loop.
    const autoRefreshTried = useRef(false);

    const loadMeta = useCallback(async () => {
        try {
            const [filters, status] = await Promise.all([
                marginCorrectionsApi.getFilters(COMPANY),
                marginCorrectionsApi.getLotOriginStatus(COMPANY),
            ]);
            setFilterOptions(filters);
            setLotStatus(status);
            return status;
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load page metadata', 'error');
            return null;
        }
    }, [toast]);

    const rebuildLots = useCallback(async () => {
        setRebuilding(true);
        try {
            const status = await marginCorrectionsApi.refreshLotOrigin(COMPANY);
            setLotStatus(status);
            if (status.hasData) {
                toast(`Lot origin refreshed — ${status.linkCount} links`, 'success');
                setRefreshKey(k => k + 1);
            } else {
                toast(status.errorMessage || 'Lot rebuild did not complete', 'error');
            }
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Lot rebuild failed', 'error');
            // Reflect the failed run row rather than leaving the banner on "in progress".
            marginCorrectionsApi.getLotOriginStatus(COMPANY).then(setLotStatus).catch(() => {});
        } finally {
            setRebuilding(false);
        }
    }, [toast]);

    useEffect(() => {
        loadMeta().then(status => {
            if (!status || !canEdit || autoRefreshTried.current) return;
            // Auto-refresh ONCE per mount, and only when it is safe and useful:
            //  - 'never-run': there is no data at all, build it.
            //  - success but stale (> threshold): refresh it.
            // Deliberately NOT on 'running' (someone is already building it — the DB guard would
            // no-op anyway) nor on 'failed' (don't silently retry a failing rebuild on every visit;
            // the user retries via the button and sees the error).
            const shouldBuild = status.status === 'never-run' || (status.hasData && status.isStale);
            if (shouldBuild) {
                autoRefreshTried.current = true;
                rebuildLots();
            }
        });
    }, [loadMeta, rebuildLots, canEdit]);

    if (permLoading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
    }

    if (!canView) {
        return <div className="p-8 text-center text-gray-500">You do not have access to Margin Corrections.</div>;
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900">Margin Corrections</h1>
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">dit</span>
                    {canEdit && <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">Edit Access</span>}
                </div>
                <button onClick={() => setRefreshKey(k => k + 1)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {/* Lot-origin status banner. `rebuilding` is this session's own POST in flight;
                otherwise reflect the actual run status (another session may be building it). */}
            {lotStatus && (() => {
                const isRunning = rebuilding || lotStatus.status === 'running';
                const isFailed = !isRunning && lotStatus.status === 'failed';
                const isNever = !isRunning && lotStatus.status === 'never-run';
                const tone = isRunning ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : isFailed ? 'bg-red-50 border-red-200 text-red-700'
                    : isNever ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : lotStatus.isStale ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-gray-50 border-gray-200 text-gray-600';
                return (
                    <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm ${tone}`}>
                        <span className="flex items-center gap-2">
                            {isRunning ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : isFailed ? <AlertTriangle className="w-4 h-4" />
                                : isNever ? <AlertTriangle className="w-4 h-4" />
                                : lotStatus.isStale ? <Clock className="w-4 h-4" />
                                : <Database className="w-4 h-4" />}
                            {isRunning ? 'Rebuilding lot origin from BC… (~15 s)'
                                : isFailed ? <>Last rebuild failed{lotStatus.errorMessage ? `: ${lotStatus.errorMessage}` : ''}. The origin panel may be out of date — retry.</>
                                : isNever ? 'Lot origin has never been built. Rebuild to see line provenance.'
                                : <>Lot origin: {lotStatus.linkCount} links{lotStatus.fromDate && <> from {new Date(lotStatus.fromDate).toLocaleDateString('de-CH')}</>}{lotStatus.ageHours != null && <> · {lotStatus.ageHours < 1 ? 'just now' : `${Math.round(lotStatus.ageHours)} h ago`}</>}
                                    {lotStatus.unreconciledLineCount != null && lotStatus.unreconciledLineCount > 0 && <span className="text-amber-700 ml-1">· {lotStatus.unreconciledLineCount} unreconciled</span>}</>}
                        </span>
                        {canEdit && !isRunning && (
                            <button onClick={rebuildLots} className="flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded-md bg-white hover:bg-gray-50 text-gray-700">
                                <RefreshCw className="w-3.5 h-3.5" /> Rebuild lot origin
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'invoice-lines' && (
                        <InvoiceLinesTab company={COMPANY} filterOptions={filterOptions} canEdit={canEdit} refreshKey={refreshKey} onDataChanged={() => setRefreshKey(k => k + 1)} />
                    )}
                    {activeTab === 'corrections' && (
                        <CorrectionsTab company={COMPANY} canEdit={canEdit} refreshKey={refreshKey} onDataChanged={() => setRefreshKey(k => k + 1)} />
                    )}
                    {activeTab === 'special-lots' && (
                        <SpecialLotsTab company={COMPANY} filterOptions={filterOptions} canEdit={canEdit} refreshKey={refreshKey} onDataChanged={() => setRefreshKey(k => k + 1)} />
                    )}
                </div>
            </div>
        </div>
    );
}
